import { Edit, Save, Cancel } from '@mui/icons-material';
import { CustomDatePicker } from '../../../../components';

import './SchedulerSubstageRow.css';
import {
  formatDate,
  formatDateTime,
  formatTime,
  statusLabels,
  statusOptions,
  substageTypeLabels,
  editableStatuses,
  isDue
} from '../../shedular.utils';
import { SchedulerSubstageRowProps } from './SchedulerSubstageRow.types';

/**
 * Scheduler substage row component displaying a single substage
 * Shows substage details with inline editing for notification time
 *
 * Features:
 * - Substage type display with label translation
 * - Time picker for notification substages in processing status
 * - Status dropdown editing for processing substages
 * - Edit/save/cancel action buttons
 * - Status-based row styling
 *
 * @param props - Component props containing substage data and callbacks
 * @returns JSX element containing the substage row
 */
export function SchedulerSubstageRow({
  substage,
  onStartEditing,
  onCancelEditing,
  onSaveEdit,
  onUpdateEditingValue,
  isEditing,
  getEditingValue,
  hasItemChanges
}: SchedulerSubstageRowProps) {
  const isEditMode = isEditing('substage', substage.id);
  const editingValue = getEditingValue('substage', substage.id);
  const substageDue = isDue(substage.scheduled_datetime);

  return (
    <tr className={`scheduler-substage-row scheduler-row-${substage.status}`}>
      <td className="scheduler-substage-cell scheduler-col-expand"></td>
      <td className="scheduler-substage-cell scheduler-col-stage">
        <span>{substageTypeLabels[substage.type as keyof typeof substageTypeLabels]}</span>
      </td>
      <td className="scheduler-substage-cell scheduler-col-date">{formatDate(substage.scheduled_datetime)}</td>
      <td className="scheduler-substage-cell scheduler-col-time">
        {isEditMode && substage.type === 'notification' && substage.status === 'processing' ? (
          <CustomDatePicker
            value={new Date(editingValue?.scheduled_datetime || substage.scheduled_datetime)}
            onChange={val => {
              if (val) {
                const timePart = (val as string).split('T')[1];
                const [h, m, s] = timePart.split(':').map(Number);
                const date = new Date(substage.scheduled_datetime);
                date.setHours(h, m, s ?? 0);
                onUpdateEditingValue('substage', substage.id, 'scheduled_datetime', date.toISOString());
              }
            }}
            showTime={true}
            includeSeconds={true}
            outputFormat="datetime-local"
            className="scheduler-time-picker"
          />
        ) : (
          formatTime(substage.scheduled_datetime)
        )}
      </td>
      <td className="scheduler-substage-cell scheduler-col-status">
        {isEditMode ? (
          <select
            value={editingValue?.status || substage.status}
            onChange={e => onUpdateEditingValue('substage', substage.id, 'status', e.target.value)}
            disabled={substage.status !== 'processing'}
            className="scheduler-edit-select"
          >
            {statusOptions
              .filter((status: string) => editableStatuses.includes(status))
              .map((status: string) => (
                <option key={status} value={status}>
                  {statusLabels[status]}
                </option>
              ))}
          </select>
        ) : (
          <span className={`scheduler-status-chip ${substage.status}`}>{statusLabels[substage.status]}</span>
        )}
      </td>
      <td className="scheduler-substage-cell scheduler-col-user">{substage.user_name}</td>
      <td className="scheduler-substage-cell scheduler-col-edit-date">{formatDateTime(substage.edit_date)}</td>
      <td className="scheduler-substage-cell scheduler-col-actions">
        {substage.status === 'processing' && !substageDue &&
          (isEditMode ? (
            <div className="scheduler-action-buttons">
              <button
                className={`scheduler-action-button scheduler-save-action-button ${!hasItemChanges('substage', substage.id) ? 'disabled' : ''}`}
                onClick={() => onSaveEdit('substage', substage.id)}
                disabled={!hasItemChanges('substage', substage.id)}
              >
                <Save sx={{ fontSize: '16px' }} />
              </button>
              <button
                className="scheduler-action-button scheduler-cancel-action-button"
                onClick={() => onCancelEditing('substage', substage.id)}
              >
                <Cancel sx={{ fontSize: '16px' }} />
              </button>
            </div>
          ) : (
            <button
              className="scheduler-action-button scheduler-edit-button"
              onClick={() =>
                onStartEditing('substage', substage.id, {
                  status: substage.status,
                  scheduled_datetime: substage.scheduled_datetime
                })
              }
            >
              <Edit sx={{ fontSize: '16px' }} />
            </button>
          ))}
      </td>
    </tr>
  );
}
